import { ConnectionPositionPair, Overlay } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import type { TemplateRef } from '@angular/core';
import { Component, EventEmitter, Input, Output, ViewContainerRef } from '@angular/core';
import { makeProvider } from '@shared/form-fields/make-provider';
import type { Media } from '@shared/modules/media/media';
import { MediaPresenter } from '@shared/modules/media/media.presenter';
import type { PendingFile } from '@shared/modules/media/pending-file';
import { PendingFileStatus } from '@shared/modules/media/pending-file';
import { AbstractInputComponent } from '../abstract-value-accessor/abstract-value';

const defaultAllowedTypes: string[] = [
    'text/plain',
    'image/*',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-office',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/pdf',
    'application/vnd.ms-outlook',
    'message/rfc822',
];

@Component({
    selector: 'app-input-multi-file',
    templateUrl: './file-input.component.html',
    styleUrls: ['./file-input.component.scss'],
    providers: [makeProvider(FileInputComponent)]
})
export class FileInputComponent extends AbstractInputComponent<Media[]> {
    public acceptTypes = defaultAllowedTypes.toString();
    private _allowedTypes: RegExp[] = this.createAllowedTypesRegex(defaultAllowedTypes);

    @Input() pendingFiles: PendingFile[] = [];
    @Input() set allowedTypes(value: string[]) {
        this._allowedTypes = this.createAllowedTypesRegex(value);
        this.acceptTypes = value.toString();
    }

    @Input() allowRemovePending = false;
    @Input() pendingSubheader = 'Uploading';
    @Input() existingSubheader = 'Existing documents';
    @Input() readonly = false;
    @Input() maxSize!: string;

    @Output() upload: EventEmitter<PendingFile[]> = new EventEmitter();
    @Output() editFile: EventEmitter<Media> = new EventEmitter();
    @Output() deleteFile: EventEmitter<Media> = new EventEmitter();
    @Output() removePending: EventEmitter<{ file: PendingFile; index: number }> = new EventEmitter();

    public invalidFiles: PendingFile[] = [];
    public isDragOver = false;
    public overlayRef = this.overlay.create({
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        maxHeight: '256px'
    });

    constructor(
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef
    ) {
        super();
    }

    private createAllowedTypesRegex(types: string[]): RegExp[] {
        return types.map(t => {
            const expression = t.replace('/', '\\/').replace('*', '.*');
            return new RegExp(expression);
        });
    }

    onFileInputChanged($event: Event) {
        const target = $event.target as HTMLInputElement;
        const fileList = target.files;

        this.addStagedFiles(fileList);
    }

    addStagedFiles(fileList: FileList) {
        const fileListArray = Array.from(fileList);
        const files: PendingFile[] = fileListArray.map(file => ({ name: file.name, file, status: PendingFileStatus.pending }));
        const validFiles = files.filter(f => this._allowedTypes.some(re => re.test(f.file.type)));
        if (validFiles.length !== files.length) {
            this.invalidFiles = files.filter(f => !this._allowedTypes.some(re => re.test(f.file.type)));
        }
        this.upload.emit(validFiles);
    }

    drop(ev: DragEvent) {
        ev.preventDefault();
        this.isDragOver = false;
        const files = ev.dataTransfer.files;

        this.addStagedFiles(files);
    }

    dragOver(event: DragEvent) {
        event.preventDefault();
        this.isDragOver = true;
    }

    removeStagedFile(index: number) {
        this.pendingFiles.splice(index, 1);
    }

    openOverlay(connectedTo: HTMLElement, template: TemplateRef<any>) {
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(connectedTo).withPositions([
                new ConnectionPositionPair(
                    { originX: 'start', originY: 'top' },
                    { overlayX: 'start', overlayY: 'bottom' },
                ),
                new ConnectionPositionPair(
                    { originX: 'start', originY: 'bottom' },
                    { overlayX: 'start', overlayY: 'top' },
                )
            ])
            .withPush(true);

        this.overlayRef.updatePositionStrategy(positionStrategy);

        const templatePortal: TemplatePortal = new TemplatePortal(template, this.viewContainerRef);
        this.overlayRef.attach(templatePortal);
    }

    closeOverlay() {
        this.overlayRef.detach();
    }

    clearInvalidFile(index: number) {
        this.invalidFiles.splice(index, 1);
    }

    protected transformValue(v: Media[]): Media[] {
        return MediaPresenter.attachCollection(super.transformValue(v));
    }
}
